/**
 * Punch coverage — how one of today's punches lines up with the scheduled
 * event windows, plus the prefill for requesting exception time that would
 * cover the uncovered part of a punch. Pure (no `Date.now()`), so it's
 * unit-testable.
 */

import {
  addMinutes,
  format,
  setHours,
  setMinutes,
  startOfDay,
  startOfMinute,
} from 'date-fns';

import { Shift, ShiftEvent } from '@/utils/shifts';

/** Minutes of slack before a punch counts as off its scheduled window. */
export const COVERAGE_GRACE_MIN = 15;

/** Backend minimum length for an exception event. */
export const MIN_EXCEPTION_MIN = 15;

export const NOT_ON_SCHEDULE = 'Not on your schedule';

export interface CoveragePunch {
  inDate: Date;
  outDate: Date | null;
}

export interface SchedWindow {
  start: Date;
  end: Date;
}

export interface TimeRange {
  start: Date;
  end: Date;
}

export type PunchCoverage =
  | { kind: 'uncovered'; label: string }
  /** Clocked out after the schedule ended — `gap` is the unscheduled (unpaid) tail. */
  | { kind: 'ranLong'; label: string; gap: TimeRange; scheduledEnd: Date }
  | { kind: 'mismatch'; label: string };

const statusOf = (e: ShiftEvent) => (e.status ?? '').toUpperCase();

/** DENIED requests / ABSENT (subbed-out) events don't put anyone on the schedule. */
const cancelsOut = (e: ShiftEvent) => statusOf(e) === 'DENIED' || statusOf(e) === 'ABSENT';

const HHMM_RE = /^(\d{1,2}):(\d{2})/;

const atTime = (day: Date, hhmm?: string): Date | null => {
  const m = hhmm ? HHMM_RE.exec(hhmm) : null;
  if (!m) return null;
  return setMinutes(setHours(startOfDay(day), Number(m[1])), Number(m[2]));
};

const minutesBetween = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / 60000);

/**
 * Scheduled event windows for the day — one per shift, spanning its events.
 * Requested (PENDING) exception time counts the same as approved time: approval
 * is a manager formality staff aren't asked to track. DENIED / ABSENT events are
 * dropped (a defensive mirror of the backend filter).
 */
export const windowsFromShifts = (shifts: Shift[]): SchedWindow[] => {
  const out: SchedWindow[] = [];
  for (const s of shifts) {
    if (!s.scheduled) continue;
    const live = s.events.filter((e) => !cancelsOut(e));
    if (s.events.length > 0 && live.length === 0) continue;

    const timed = live
      .map((e) => ({ start: atTime(s.scheduledStart, e.start), end: atTime(s.scheduledStart, e.end) }))
      .filter((t): t is { start: Date; end: Date } => !!t.start && !!t.end);

    if (timed.length === 0 || timed.length < live.length) {
      // No usable per-event times — fall back to the shift's own window.
      out.push({ start: s.scheduledStart, end: s.scheduledEnd });
      continue;
    }
    out.push({
      start: new Date(Math.min(...timed.map((t) => t.start.getTime()))),
      end: new Date(Math.max(...timed.map((t) => t.end.getTime()))),
    });
  }
  return out;
};

/** Parts of `range` not covered by any of `windows`, in time order. */
const uncoveredParts = (range: TimeRange, windows: SchedWindow[]): TimeRange[] => {
  const parts: TimeRange[] = [];
  let cursor = range.start;
  const sorted = windows
    .filter((w) => w.start < range.end && w.end > range.start)
    .sort((a, b) => a.start.getTime() - b.start.getTime());
  for (const w of sorted) {
    if (w.start > cursor) parts.push({ start: cursor, end: w.start });
    if (w.end > cursor) cursor = w.end;
  }
  if (cursor < range.end) parts.push({ start: cursor, end: range.end });
  return parts;
};

/**
 * A punch's coverage vs. the scheduled event windows: null = on-schedule;
 * `uncovered` = no overlapping event; `ranLong` = clocked out past the end of
 * the schedule with nothing covering the extra time (it isn't paid);
 * `mismatch` = 'Started Nm late' / 'Left Nm early'.
 */
export const coverageForPunch = (
  p: CoveragePunch,
  windows: SchedWindow[]
): PunchCoverage | null => {
  const end = p.outDate ?? p.inDate;
  // Start-inclusive overlap ([win.start, win.end)): a punch clocked in exactly at
  // the shift start — where an open punch has end === inDate — is on schedule, not off it.
  const w = windows.find((win) => p.inDate < win.end && end >= win.start);
  if (!w) return { kind: 'uncovered', label: NOT_ON_SCHEDULE };
  const lateIn = minutesBetween(w.start, p.inDate);
  if (lateIn > COVERAGE_GRACE_MIN) return { kind: 'mismatch', label: `Started ${lateIn}m late` };
  if (p.outDate) {
    if (minutesBetween(w.end, p.outDate) > COVERAGE_GRACE_MIN) {
      // The tail past this window — less anything else on the schedule — is the
      // time worked with no event behind it.
      const gap = uncoveredParts({ start: w.end, end: p.outDate }, windows).find(
        (g) => minutesBetween(g.start, g.end) > COVERAGE_GRACE_MIN
      );
      return gap
        ? {
            kind: 'ranLong',
            label: `Ran ${minutesBetween(gap.start, gap.end)}m long`,
            gap,
            scheduledEnd: w.end,
          }
        : null;
    }
    const early = minutesBetween(p.outDate, w.end);
    if (early > COVERAGE_GRACE_MIN) return { kind: 'mismatch', label: `Left ${early}m early` };
  }
  return null;
};

export interface ExceptionPrefill {
  /** yyyy-MM-dd */
  date: string;
  /** HH:mm (24-hour) */
  start: string;
  /** HH:mm (24-hour) */
  end: string;
}

/**
 * Exception-time fields covering `start` → `end`. An open-ended range (a punch
 * still on the clock) ends at `now` rounded up to the next quarter hour (the
 * staffer adjusts it if they'll stay longer). Always at least the backend
 * minimum, and never past the end of the day.
 */
export const exceptionPrefillForRange = (
  startAt: Date,
  endAt: Date | null,
  now: Date
): ExceptionPrefill => {
  const start = startOfMinute(startAt);
  let end: Date;
  if (endAt) {
    end = startOfMinute(endAt);
  } else {
    const n = startOfMinute(now);
    const over = n.getMinutes() % 15;
    end = over === 0 ? n : addMinutes(n, 15 - over);
  }
  const minEnd = addMinutes(start, MIN_EXCEPTION_MIN);
  if (end < minEnd) end = minEnd;
  const lastMinute = setMinutes(setHours(start, 23), 59);
  if (end > lastMinute) end = lastMinute;
  return {
    date: format(start, 'yyyy-MM-dd'),
    start: format(start, 'HH:mm'),
    end: format(end, 'HH:mm'),
  };
};

/** Prefill covering a whole unscheduled punch (clock-in → clock-out). */
export const exceptionPrefillForPunch = (p: CoveragePunch, now: Date): ExceptionPrefill =>
  exceptionPrefillForRange(p.inDate, p.outDate, now);
